import ReservationBadge from '@/features/reservations/components/ReservationBadge';
import { useReservationList } from '@/features/reservations/hooks/useReservations';
import { overlappingReservations } from '@/features/reservations/overlapping-reservations';
import { useReservationConfig } from '@/features/reservation-setup/hooks/useReservationConfig';
import { useT } from '@/i18n';
import { formatTime, fromApi } from '@/utils/datetime';
import type { Reservation } from '@restaurant/api-client';
import { ReservationStatus } from '@restaurant/types';
import { AccordionContent, List, ListItem } from 'framework7-react';

export default function ReservationConflictNotice({ reservation }: { reservation: Reservation }) {
  const { data: config } = useReservationConfig();
  if (config?.autoConfirmMode !== 'MANUAL' || reservation.status !== ReservationStatus.PENDING) {
    return null;
  }
  return <ConflictList reservation={reservation} />;
}

function ConflictList({ reservation }: { reservation: Reservation }) {
  const t = useT();
  const bookingDt = fromApi(reservation.time);
  const bookingDay = bookingDt.toFormat('yyyy-MM-dd');
  const endDay = bookingDt.plus({ minutes: reservation.duration }).toFormat('yyyy-MM-dd');
  const crossesMidnight = endDay !== bookingDay;
  const { data } = useReservationList(bookingDay);
  const { data: nextDayData } = useReservationList(crossesMidnight ? endDay : bookingDay);
  const candidates = crossesMidnight
    ? [...(data?.data ?? []), ...(nextDayData?.data ?? [])]
    : (data?.data ?? []);
  const conflicts = overlappingReservations(reservation, candidates);
  if (conflicts.length === 0) return null;

  return (
    <List strongIos insetIos dividersIos accordionList className="rsv-conflict-highlight">
      <ListItem accordionItem title={t.reservations.detail.conflict(conflicts.length)}>
        <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
          exclamationmark_triangle_fill
        </i>
        <AccordionContent>
          <List dividersIos>
            {conflicts.map((r) => (
              <ListItem
                key={r.id}
                title={r.guest.name}
                footer={`${formatTime(r.time)}–${fromApi(r.time)
                  .plus({ minutes: r.duration })
                  .toFormat('HH:mm')} · ${r.partySize} ${
                  r.partySize === 1 ? t.reservations.guest : t.reservations.guests
                }`}
              >
                <div slot="after">
                  <ReservationBadge
                    status={r.status}
                    arrivalStatus={r.arrivalStatus}
                    reservationTime={r.time}
                  />
                </div>
              </ListItem>
            ))}
          </List>
        </AccordionContent>
      </ListItem>
    </List>
  );
}
